import { useCallback, useEffect, useId, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { IconClose } from './icons';

export function Field({ label, hint, children }: { label: string; hint?: ReactNode; children: (id: string) => ReactNode }) {
  const id = useId();
  return (
    <div className="tmd-field">
      <div className="tmd-field-head">
        <label htmlFor={id}>{label}</label>
        {hint != null && <span className="tmd-field-hint">{hint}</span>}
      </div>
      {children(id)}
    </div>
  );
}

export function TextField(props: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  maxLength?: number;
  autoFocus?: boolean;
}) {
  const { label, value, onChange, placeholder, maxLength } = props;
  return (
    <Field label={label} hint={maxLength && value.length > maxLength * 0.7 ? `${value.length}/${maxLength}` : undefined}>
      {(id) => (
        <input
          id={id}
          className="tmd-input"
          type="text"
          value={value}
          placeholder={placeholder}
          maxLength={maxLength}
          autoFocus={props.autoFocus}
          autoComplete="off"
          onChange={(e) => onChange(e.target.value)}
        />
      )}
    </Field>
  );
}

export function Segmented<T extends string>({ value, options, onChange, label }: {
  value: T;
  options: Array<{ value: T; label: ReactNode; title?: string; disabled?: boolean }>;
  onChange: (v: T) => void;
  label: string;
}) {
  return (
    <div className="tmd-seg" role="radiogroup" aria-label={label}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={o.value === value}
          title={o.title}
          disabled={o.disabled}
          className={o.value === value ? 'is-on' : ''}
          onClick={() => onChange(o.value)}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

export function Toggle({ checked, onChange, children }: { checked: boolean; onChange: (v: boolean) => void; children: ReactNode }) {
  return (
    <label className="tmd-toggle">
      <span>{children}</span>
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <i aria-hidden="true" />
    </label>
  );
}

export function Modal({ title, onClose, children, wide }: { title: string; onClose: () => void; children: ReactNode; wide?: boolean }) {
  // Render at the editor root: on small screens the cart bar and stage are sticky with their own
  // z-index, so a modal left inside one is painted under the other and its header (and close) hidden.
  const [root, setRoot] = useState<Element | null>(null);
  const anchor = useCallback((el: HTMLSpanElement | null) => el && setRoot(el.closest('.tmd') ?? document.body), []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);
  if (!root) return <span hidden ref={anchor} />;
  return createPortal(
    <div className="tmd-modal" role="dialog" aria-modal="true" aria-label={title} onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`tmd-modal-box${wide ? ' is-wide' : ''}`}>
        <div className="tmd-modal-head">
          <h3>{title}</h3>
          <button type="button" className="tmd-icon-btn" onClick={onClose} aria-label="Close">
            <IconClose />
          </button>
        </div>
        <div className="tmd-modal-body">{children}</div>
      </div>
    </div>,
    root,
  );
}
